import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface OnboardingModalProps {
  showOnboarding: boolean;
  setShowOnboarding: (show: boolean) => void;
  userEmail: string;
}

// Helpers: derive a friendly display name from an email address
const capitalize = (s: string) =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const formatNameFromEmail = (email?: string) => {
  if (!email) return "";
  const local = email.split("@")[0].split("+")[0];
  // Replace delimiters with spaces, split into words, capitalize
  const words = local
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter(Boolean);
  const name = words.map(capitalize).join(" ");
  return name || email;
};

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  showOnboarding,
  setShowOnboarding,
  userEmail,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: "",
    interests: [] as string[],
    bio: "",
    profilePicture: null as File | null,
    claimSignupBonus: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const steps = [
    {
      title: "Welcome To",
      platform: "My!hū",
      subtitle: "Let's get you set up with a great profile",
      component: WelcomeStep,
    },
    {
      title: "Tell us about yourself",
      subtitle: "Basic information to personalize your experience",
      component: ProfileStep,
    },
    {
      title: "What interests you?",
      subtitle: "Help us recommend content you'll love",
      component: InterestsStep,
    },
    {
      title: "Add a profile picture",
      subtitle: "Make your profile stand out",
      component: PhotoStep,
    },
    {
      title: "You're all set!",
      subtitle: "Welcome to the My!hū community",
      component: CompleteStep,
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setError(""); // Clear any errors when moving to next step
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(""); // Clear any errors when moving to previous step
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData.user)
        throw userError || new Error("No user found");

      const user = userData.user;

      // Handle profile picture upload first if provided
      let avatarFileName = null;
      if (formData.profilePicture) {
        // Validate file type and size (same as Profile.tsx)
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        if (!allowedTypes.includes(formData.profilePicture.type)) {
          throw new Error(
            "Please upload a valid image file (JPG, PNG, GIF, or WebP)",
          );
        }
        if (formData.profilePicture.size > 5 * 1024 * 1024) {
          throw new Error("File size should be less than 5MB");
        }

        const fileExt = formData.profilePicture.name.split(".").pop();
        avatarFileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(2)}${fileExt ? `.${fileExt}` : ""}`;

        // Upload to avatars bucket
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(avatarFileName, formData.profilePicture, {
            upsert: true,
            contentType: formData.profilePicture.type,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw uploadError;
        }
      }

      // Update/insert profile data in the profiles table (same structure as Profile.tsx)
      const profileData: any = {
        id: user.id,
        full_name: formData.fullName,
        updated_at: new Date().toISOString(),
      };

      // Add optional fields only if they have values
      if (avatarFileName) {
        profileData.avatar_url = avatarFileName;
      }
      if (formData.dateOfBirth) {
        profileData.date_of_birth = formData.dateOfBirth;
      }
      if (formData.bio) {
        profileData.bio = formData.bio;
      }
      if (formData.interests.length > 0) {
        profileData.interests = formData.interests;
      }

      // Safer: avoid upsert that may null omitted columns like credits_balance.
      // Prefer update when profile exists, otherwise insert.
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      let profileError = null;
      if (existingProfile) {
        const updates: any = {
          full_name: profileData.full_name,
          updated_at: profileData.updated_at,
        };
        if (profileData.avatar_url) updates.avatar_url = profileData.avatar_url;
        if (profileData.date_of_birth)
          updates.date_of_birth = profileData.date_of_birth;
        if (profileData.bio) updates.bio = profileData.bio;
        if (profileData.interests) updates.interests = profileData.interests;

        const { error: err } = await supabase
          .from("profiles")
          .update(updates)
          .eq("id", user.id);
        profileError = err;
      } else {
        const { error: err } = await supabase
          .from("profiles")
          .insert([profileData]);
        profileError = err;
      }

      if (profileError) {
        console.error("Profile update error:", profileError);
        throw profileError;
      }

      // Update user metadata to mark onboarding as completed
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          onboarding_completed: true,
        },
      });

      if (metadataError) {
        console.error("Metadata update error:", metadataError);
        throw metadataError;
      }

      // Show success message
      toast({
        title: "Welcome to My!hū!",
        description: "Your profile has been set up successfully.",
      });

      // Persist signup claim preference locally so the user can claim after login
      try {
        if (formData.claimSignupBonus) {
          const pending = {
            user_id: user.id,
            pending: true,
            created_at: new Date().toISOString(),
          };
          if (typeof window !== "undefined" && window.localStorage) {
            localStorage.setItem(
              "myhu:signup_claim_pending",
              JSON.stringify(pending),
            );
          }
        }
      } catch (e) {
        console.warn("Could not persist signup claim preference locally", e);
      }

      // Also persist a server-side flag on the profiles table for cross-device reliability
      try {
        if (formData.claimSignupBonus) {
          const { error: flagError } = await supabase
            .from("profiles")
            .update({ signup_claim_pending: true })
            .eq("id", user.id);

          if (flagError) {
            // If update failed (maybe profile didn't exist), try insert minimal record
            const { error: insertErr } = await supabase
              .from("profiles")
              .insert([{ id: user.id, signup_claim_pending: true }]);
            if (insertErr) {
              console.warn(
                "Could not persist signup claim flag server-side",
                insertErr,
              );
            }
          }
        }
      } catch (e) {
        console.warn("Error writing signup claim flag to profiles:", e);
      }

      setShowOnboarding(false);
    } catch (error: any) {
      console.error("Error completing onboarding:", error);
      setError(
        error.message || "An error occurred while setting up your profile",
      );
      toast({
        variant: "destructive",
        title: "Error setting up profile",
        description:
          error.message ||
          "There was an error setting up your profile. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!showOnboarding) return null;

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <img
        src="/ocean.webp"
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover z-0"
      />
      <div className="absolute inset-0 bg-black/60 z-10" />
      {/* Photographer credit: placed above overlay (zIndex 15) so it's visible but doesn't overlap modal */}
      <div
        className="absolute left-3 bottom-3 text-xs text-white"
        style={{ zIndex: 15 }}
      >
        <a
          href="https://www.pexels.com/photo/sea-waves-hitting-rocks-414268/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Photo by Pixabay
        </a>
      </div>
      <div className="relative z-20 bg-white text-black rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Progress bar (gradient track + gradient fill) */}
        <div className="h-4 bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-300 rounded-r-lg"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h3 className="text-md font-bold text-gray-900 mb-2">
              {steps[currentStep].title}
            </h3>
            <div className="flex justify-center mb-2">
              <img
                src="/Logo.png"
                alt="Logo"
                className="w-[5rem] h-[5rem] object-cover"
              />
            </div>
            <p className="text-gray-600">{steps[currentStep].subtitle}</p>
          </div>

          <CurrentStepComponent
            formData={formData}
            setFormData={setFormData}
            userEmail={userEmail}
          />

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="px-6 bg-white hover:bg-teal-300 text-black hover:text-white border-gray-300 hover:border-teal-300"
            >
              Previous
            </Button>

            {currentStep === steps.length - 1 ? (
              <Button
                onClick={handleComplete}
                disabled={loading}
                className="px-6 bg-teal-500 hover:bg-teal-600 text-white"
              >
                {loading ? "Completing..." : "Get Started"}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="px-6 bg-teal-500 hover:bg-teal-300 text-white w-[10rem] font-semibold"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const WelcomeStep: React.FC<any> = ({ userEmail }) => {
  const displayName = formatNameFromEmail(userEmail) || "there";
  return (
    <div className="text-center py-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">Hello {displayName}!</h3>
        <p className="text-gray-600">
          Thanks for joining My!hū{displayName ? `, ${displayName}` : ""}
        </p>
      </div>
      <p className="text-gray-700">
        We'll guide you through a quick setup to personalize your experience.
        This should only take a few minutes.
      </p>
    </div>
  );
};

const ProfileStep: React.FC<any> = ({ formData, setFormData }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Full Name
      </label>
      <input
        type="text"
        value={formData.fullName}
        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#40e0d0]"
        placeholder="Enter your full name"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Date of Birth
      </label>
      <input
        type="date"
        value={formData.dateOfBirth}
        title="Date of Birth"
        placeholder="YYYY-MM-DD"
        onChange={(e) =>
          setFormData({ ...formData, dateOfBirth: e.target.value })
        }
        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#40e0d0]"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Bio (Optional)
      </label>
      <textarea
        value={formData.bio}
        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#40e0d0]"
        placeholder="Tell us a bit about yourself..."
        rows={3}
      />
    </div>
  </div>
);

const InterestsStep: React.FC<any> = ({ formData, setFormData }) => {
  const availableInterests = [
    "Environmental Science",
    "Climate Change",
    "Wildlife Conservation",
    "Sustainable Living",
    "Renewable Energy",
    "Afro-Ecological Heritage",
    "Biodiversity",
    "Ocean Conservation",
    "Forest Protection",
    "Carbon Footprint",
    "Green Technology",
    "Eco-Tourism",
    "Permaculture",
    "Marine Biology",
    "Ecology",
    "Environmental Policy",
  ];

  const toggleInterest = (interest: string) => {
    const current = formData.interests;
    if (current.includes(interest)) {
      setFormData({
        ...formData,
        interests: current.filter((i) => i !== interest),
      });
    } else {
      setFormData({
        ...formData,
        interests: [...current, interest],
      });
    }
  };

  return (
    <div>
      <p className="text-gray-600 mb-6">
        Select environmental topics you're passionate about (choose as many as
        you like):
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
        {availableInterests.map((interest) => (
          <button
            key={interest}
            onClick={() => toggleInterest(interest)}
            className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
              formData.interests.includes(interest)
                ? "bg-[#40e0d0] text-white border-[#40e0d0]"
                : "bg-white text-gray-700 border-gray-300 hover:border-[#40e0d0]"
            }`}
          >
            {interest}
          </button>
        ))}
      </div>
    </div>
  );
};

const PhotoStep: React.FC<any> = ({ formData, setFormData }) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size (same validation as Profile.tsx)
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, PNG, GIF, or WebP)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }
      setFormData({ ...formData, profilePicture: file });
    }
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        {formData.profilePicture ? (
          <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-gray-200">
            <img
              src={URL.createObjectURL(formData.profilePicture)}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center">
            <span className="text-[7rem] text-gray-400">📷</span>
          </div>
        )}
      </div>

      <input
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        className="hidden"
        id="profile-picture"
      />

      <div className="space-y-4">
        <label
          htmlFor="profile-picture"
          className="inline-block px-6 py-3 text-teal-500 rounded-lg cursor-pointer hover:text-teal-700 transition-colors hover:text-[1.2rem] transition-smooth"
        >
          {formData.profilePicture ? "Change Photo" : "Choose Photo"}
        </label>

        {formData.profilePicture && (
          <div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, profilePicture: null })}
              className="block mx-auto px-4 py-2 text-sm text-red-600 hover:text-red-800 transition-colors"
            >
              Remove Photo
            </button>
          </div>
        )}

        <p className="text-gray-500 text-sm">
          Supported formats: JPG, PNG, GIF, WebP (max 5MB)
          <br />
          Don't worry, you can always change this later in your profile settings
        </p>
      </div>
    </div>
  );
};

const CompleteStep: React.FC<any> = ({ formData, setFormData }) => {
  const { toast } = useToast();

  const toggleClaim = (val: boolean) => {
    setFormData({ ...formData, claimSignupBonus: val });
  };

  return (
    <div className="text-center py-8">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8">
        <span className="text-8xl">🎉</span>
      </div>
      <h3 className="text-xl font-semibold mb-4">Perfect! You're all set</h3>

      <div className="mb-4">
        <div className="flex items-center justify-center gap-3 mb-4">
          <input
            id="claim-signup"
            type="checkbox"
            checked={!!formData.claimSignupBonus}
            onChange={(e) => toggleClaim(e.target.checked)}
            className="h-4 w-4"
          />
          <label
            htmlFor="claim-signup"
            className="text-sm text-gray-700 font-semibold"
          >
            Claim 10 signup credits
          </label>
        </div>

        <p className="text-sm text-gray-500">
          If you check this box, you can claim your 10 signup credits from the
          homepage after you finish onboarding.
        </p>
      </div>

      <p className="text-sm text-gray-500">
        Click "Get Started" to begin exploring My!hū and start your
        environmental journey
      </p>
    </div>
  );
};

export default OnboardingModal;
